import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import ViewInvoice from '@/containers/Program/View/components/invoice/ViewInvoice';
import { QUIET_REQUEST } from '@/service/fundingApprovals';

const errorText = (res) => {
  const e = res?.data?.errors ?? res?.data?.message ?? res?.data;
  return (typeof e === 'string' && e) || 'The invoice could not be loaded.';
};

// Invoice number rendered as a link-styled button; clicking fetches the invoice and opens the same
// "Invoice Details" modal the Deposit report uses. The axios interceptor resolves 4xx responses, so
// the status is checked explicitly and an inline error shown instead of opening an empty modal.
// onClosed lets the caller refetch: Cancel / Retry release inside ViewInvoice change the hold.
// deleted: the invoice read route doesn't resolve a deleted invoice or program, so there is nothing to open.
// Pay Invoice is hidden: paying needs the invoice page's payment step, which this modal doesn't have.
// modal-program modal-lg: same modal classes as Program → Billing, which undo the global centered modal text.
const InvoiceDetailsLink = ({ organizationId, programId, invoiceId, onClosed, deleted = false, children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [isOpen, setOpen] = useState(false);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const open = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/organization/${organizationId}/program/${programId}/invoice/${invoiceId}`, QUIET_REQUEST);
      if (!mounted.current) return;
      if (!res || res.status >= 400 || !res.data || res.data.errors) {
        setError(errorText(res));
      } else {
        setInvoice(res.data);
        setOpen(true);
      }
    } catch (e) {
      if (mounted.current) setError(errorText(e?.response));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    onClosed && onClosed();
  };

  if (deleted) return <span>{children} (deleted)</span>;

  return (
    <>
      <Button color="link" className="p-0 align-baseline" onClick={open} disabled={loading}>
        {loading ? 'Loading...' : children}
      </Button>
      {error && <div className="text-danger small" role="alert">{error}</div>}
      {isOpen && invoice && (
        <Modal isOpen toggle={close} className="modal-program modal-lg">
          <ModalHeader toggle={close}>Invoice Details</ModalHeader>
          <ModalBody>
            <ViewInvoice
              invoice={invoice}
              {...invoice}
              program={{ id: programId, organization_id: organizationId }}
              setStep={close}
              hidePayInvoice
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default InvoiceDetailsLink;
